/**
 * Column mapping utilities for processing and cleaning data
 * These functions transform raw column data into standardized formats
 */

/**
 * Maps party input to standard party letters
 * @param {string} party - The party input to standardize
 * @param {boolean} blankToNP - Optional, if true, will return 'N' if party is blank
 * @returns {string} Standardized party letter or throws error if not classified
 */
export function mapParty(party, blankToNP = false) {
  if (!party && blankToNP) {
    party = 'N'
  }
  let partyMod = (party || '').replaceAll(' Party', '');
  switch((partyMod || '').toUpperCase()) {
    case 'REPUBLICAN':
    case 'REP':
    case 'GOP':
    case 'G.O.P.':
    case 'MAGA REPUBLICAN':
    case 'CULTURE REPUBLICAN':
    case 'CONSERVATIVE REPUBLICAN':
    case 'CONSERVATIVE':
    case 'R':
      return 'R';
    case 'DEMOCRAT':
    case 'DEMOCRATIC':
    case 'DEM':
    case 'D':
      return 'D';
    case 'NONPARTISAN':
    case 'NON PARTISAN':
    case 'NON-PARTISAN':
    case 'NON':
    case 'NP':
    case 'N':
    case 'UNA':
      return 'N';
    case 'GREEN':
    case 'GREEN PARTY':
    case 'INDEPENDENT GREEN PARTY':
    case 'INDEPENDENT GREEN':
    case 'IG':
    case 'G':
    case 'WISCONSIN GREEN':
    case 'WISCONSIN':
    case 'GRE':
      return 'G';
    case 'LIBERATION AND SOCIALISM':
    case 'SOCIALIST WORKERS':
    case 'SOCIALIST EQUALITY':
    case 'SOCIALISM AND LIBERATION':
    case 'Party for Socialism and Liberation'.toUpperCase():
    case 'SOCIALISM':
    case 'S':
      return 'S';
    case 'AMERICAN SOLIDARITY':
    case 'ASP':
      return 'ASP';
    case 'DEMOCRATIC / REPUBLICAN':
    case 'INDEPENDENT':
    case 'INDEPENDENT,':
    case 'Justice For All'.toUpperCase():
    case 'DISRUPT':
    case 'DISRUPT THE':
    case 'UPHOLD':
    case 'AMERICA FIRST':
    case 'INDEPENDENT CANDIDATES':
    case 'STATES NO PARTY PREFERENCE':
    case 'I':
      return 'I';
    case 'LIBERTARIAN':
    case 'LIB':
    case 'L':
    case 'IL':
      return 'L';
    case 'NO LABELS':
    case 'NO-LABELS':
      return 'NL';
    case 'OTHER':
    case 'UNKNOWN':
    case 'U':
      return 'U';
    case 'POPULIST':
      return 'P';
    case 'CONSTITUTION':
    case 'CON':
    case 'CST':
    case 'C':
      return 'C';
    case 'WE THE PEOPLE':
    case 'W':
    case 'WTP':
      return 'W';
    case 'FORWARD':
    case 'F':
      return 'F';
    default:
      return party;
  }
}

const normalizePartyValue = (party) => String(party || '')
  .replaceAll(' Party', '')
  .trim()
  .toUpperCase();

export function mapPartyWithOptions(party, partyOptions = [], blankToNP = false) {
  const mappedParty = mapParty(party, blankToNP);
  const normalizedMappedParty = normalizePartyValue(mappedParty);
  const matchingParty = (partyOptions || []).find((option) => {
    return normalizePartyValue(option?.key) === normalizedMappedParty ||
      normalizePartyValue(option?.name) === normalizedMappedParty;
  });

  return matchingParty?.key || mappedParty;
}

/**
 * Maps an input to boolean
 */
export function mapBoolean(status) {
  if (status === null || status === undefined || status === '') {
    return status;
  }
  
  if (typeof status === 'boolean') {
    return status;
  }
  
  const normalized = String(status).toLowerCase().trim();
  
  switch (normalized) {
    case 'true':
    case 'yes':
    case 'y':
    case '1':
    case 'incumbent':
    case 'current':
      return true;
    case 'false':
    case 'no':
    case 'n':
    case '0':
    case 'non-incumbent':
    case 'challenger':
      return false;
    default:
      return status; // Unknown value
  }
}

export function validateBoolean(status) {
  if(status === true || status === false) {
    return true;
  }
  return `Invalid boolean value: ${status}`;
}

/**
 * Validates party mapping (placeholder for now)
 * @param {string} party - The party to validate
 * @returns {boolean} Always returns true for now
 */
export function validateParty(party) {
  if (!party) {
    return `Invalid party: ${party}`;
  }
  return true;
}

export function validatePartyWithAllowedList(party, allowedPartyKeys = []) {
  if (!party) {
    return `Invalid party: ${party}`;
  }

  const normalizedAllowed = (allowedPartyKeys || []).map((key) => String(key || '').toUpperCase());
  if (!normalizedAllowed.length) {
    return validateParty(party);
  }

  if (!normalizedAllowed.includes(String(party).toUpperCase())) {
    return `Invalid party: ${party}`;
  }

  return true;
}

export function validateWebsite(value) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return true;
  }

  try {
    const website = String(value).trim();
    const websiteWithProtocol = /^https?:\/\//i.test(website)
      ? website
      : `https://${website}`;
    const parsedWebsite = new URL(websiteWithProtocol);
    const hostnameParts = parsedWebsite.hostname.split('.');
    const topLevelDomain = hostnameParts[hostnameParts.length - 1];

    if (
      !['http:', 'https:'].includes(parsedWebsite.protocol) ||
      hostnameParts.length < 2 ||
      topLevelDomain.length < 2
    ) {
      return 'Invalid website format';
    }
  } catch (error) {
    return 'Invalid website format';
  }

  return true;
}

export function updateRawCellValue(rows, rowIndex, columnName, value) {
  return rows.map((row, index) => (
    index === rowIndex
      ? { ...row, [columnName]: value }
      : row
  ));
}


/**
 * This section deals with functions for cleaning names. We only worry about this if the name is all uppercased
 */
const splitPrecedence = [' ', '"', '-','.'];
const protectedSegments = ['I','II','III','IV','V','VI','VII','VIII','IX','X'];

// Common name prefixes that should be handled specially
const namePrefixes = {
  // Irish/Scottish prefixes
  'MC': 'Mc',
  'MAC': 'Mac',
  'O\'': 'O\'',
  'O\'': 'O\'',
  
  // Dutch prefixes
  'VAN': 'van',
  'VON': 'von',
  'DE': 'de',
  'DER': 'der',
  'DEN': 'den',
  'VAN DER': 'van der',
  'VAN DEN': 'van den',
  'VAN DE': 'van de',
  
  // Spanish/Portuguese prefixes
  'DEL': 'del',
  'DE LA': 'de la',
  'DE LOS': 'de los',
  'DE LAS': 'de las',
  'DOS': 'dos',
  'DAS': 'das',
  'DO': 'do',
  'DA': 'da',
  
  // French prefixes
  'DU': 'du',
  'DE LA': 'de la',
  'DES': 'des',
  'LE': 'le',
  'LA': 'la',
  'LES': 'les',
  
  // Italian prefixes
  'DI': 'di',
  'DA': 'da',
  'DEL': 'del',
  'DELLA': 'della',
  'DELLO': 'dello',
  'DEI': 'dei',
  'DEGLI': 'degli',
  'DELLE': 'delle',
  
  // German prefixes
  'VON': 'von',
  'VAN': 'van',
  'ZU': 'zu',
  'ZUR': 'zur',
};

// Common suffixes that should be handled specially
const nameSuffixes = {
  'JR': 'Jr.',
  'JR.': 'Jr.',
  'JUNIOR': 'Junior',
  'SR': 'Sr.',
  'SR.': 'Sr.',
  'SENIOR': 'Senior',
  'II': 'II',
  'III': 'III',
  'IV': 'IV',
  'V': 'V',
  'VI': 'VI',
  'VII': 'VII',
  'VIII': 'VIII',
  'IX': 'IX',
  'X': 'X',
  'ESQ': 'Esq.',
  'ESQ.': 'Esq.',
  'ESQUIRE': 'Esquire',
  'PHD': 'Ph.D.',
  'PH.D': 'Ph.D.',
  'PH.D.': 'Ph.D.',
  'MD': 'M.D.',
  'M.D': 'M.D.',
  'M.D.': 'M.D.',
  'JD': 'J.D.',
  'J.D': 'J.D.',
  'J.D.': 'J.D.',
  'MBA': 'MBA',
  'MA': 'M.A.',
  'M.A': 'M.A.',
  'M.A.': 'M.A.',
  'MS': 'M.S.',
  'M.S': 'M.S.',
  'M.S.': 'M.S.',
  'BA': 'B.A.',
  'B.A': 'B.A.',
  'B.A.': 'B.A.',
  'BS': 'B.S.',
  'B.S': 'B.S.',
  'B.S.': 'B.S.',
  'CPA': 'CPA',
  'RN': 'RN',
  'LPN': 'LPN',
  'DDS': 'D.D.S.',
  'D.D.S': 'D.D.S.',
  'D.D.S.': 'D.D.S.',
};

const cleanSegment = (segment, character) => {
  const currentCharacterIndex = splitPrecedence.indexOf(character);
  const nextSplitCharacter = splitPrecedence[currentCharacterIndex + 1];
  return segment
    .split(character)
    .map(segment => {
      if(protectedSegments.includes(segment)) return segment;
      if(segment?.length < 1) return segment;
      if(nextSplitCharacter) return cleanSegment(segment, nextSplitCharacter);

      // Handle empty segments
      if (!segment || segment.length === 0) return segment;
      
      // Handle name suffixes (Jr., Sr., III, etc.)
      if (nameSuffixes[segment]) {
        return nameSuffixes[segment];
      }
      
      // Handle name prefixes (Mc, Mac, van, de, etc.)
      if (namePrefixes[segment]) {
        return namePrefixes[segment];
      }
      
      // Handle special cases for multi-word prefixes
      const upperSegment = segment.toUpperCase();
      for (const [prefix, replacement] of Object.entries(namePrefixes)) {
        if (upperSegment.startsWith(prefix + ' ')) {
          const remaining = segment.slice(prefix.length + 1);
          if (remaining.length > 0) {
            const remainingCapped = remaining.charAt(0).toUpperCase() + remaining.slice(1).toLowerCase();
            return replacement + ' ' + remainingCapped;
          }
        }
      }
      
      // Handle special cases for multi-word suffixes
      for (const [suffix, replacement] of Object.entries(nameSuffixes)) {
        if (upperSegment.endsWith(' ' + suffix)) {
          const before = segment.slice(0, -(suffix.length + 1));
          if (before.length > 0) {
            const beforeCapped = before.charAt(0).toUpperCase() + before.slice(1).toLowerCase();
            return beforeCapped + ' ' + replacement;
          }
        }
      }

      // Basic capitalization
      let segmentCapped = segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
      
      // Handle Irish/Scottish names (Mc, Mac)
      if (segmentCapped.startsWith('Mc') && segmentCapped.length > 2) {
        segmentCapped = 'Mc' + segment.slice(2, 3).toUpperCase() + segment.slice(3).toLowerCase();
      }
      if (segmentCapped.startsWith('Mac') && segmentCapped.length > 3) {
        segmentCapped = 'Mac' + segment.slice(3, 4).toUpperCase() + segment.slice(4).toLowerCase();
      }
      
      // Handle names starting with parentheses
      if (segmentCapped.startsWith('(') && segmentCapped.length > 1) {
        segmentCapped = '(' + segment.slice(1, 2).toUpperCase() + segment.slice(2).toLowerCase();
      }
      
      // Handle names ending with parentheses
      if (segmentCapped.endsWith(')') && segmentCapped.length > 1) {
        const beforeParen = segmentCapped.slice(0, -1);
        const lastChar = segmentCapped.slice(-1);
        segmentCapped = beforeParen + lastChar;
      }
      
      // Handle names with apostrophes (O'Connor, D'Angelo, etc.)
      if (segmentCapped.includes('\'') && segmentCapped.length > 1) {
        const parts = segmentCapped.split('\'');
        segmentCapped = parts.map((part, index) => {
          if (index === 0) {
            return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
          } else {
            return '\'' + part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
          }
        }).join('');
      }
      
      // Handle names with hyphens (Jean-Pierre, Mary-Jane, etc.)
      if (segmentCapped.includes('-') && segmentCapped.length > 1) {
        const parts = segmentCapped.split('-');
        segmentCapped = parts.map(part => {
          return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
        }).join('-');
      }
      
      // Handle names with periods (St., Dr., etc.)
      if (segmentCapped.includes('.') && segmentCapped.length > 1) {
        const parts = segmentCapped.split('.');
        segmentCapped = parts.map((part, index) => {
          if (index === parts.length - 1) {
            return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
          } else {
            return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() + '.';
          }
        }).join('');
      }
      
      return segmentCapped;
    })
    .join(character)
}

/* Cleans a  */
function cleanUpperCasedName(input) {
  let cleanName = input.trim().replace(/\s\s+/g, ' ').toUpperCase();
  cleanName = cleanSegment(cleanName, ' ');

  return cleanName;
}

export function mapName(name) {
  if(name.toUpperCase() === name) {
    return cleanUpperCasedName(name);
  }
  return name;
}

// Define base column mapping options with mapping and validation functions
export const candidateListColumnMappingOptions = [
  { 
    value: 'name', 
    label: 'Candidate Name', 
    required: true,
    map: (value) => mapName(value.trim()),
    validate: (value) => Boolean(value) && value.length > 0 ? true : `Candidate name is required`
  },
  { 
    value: 'groupLabel', 
    label: 'Group Label', 
    required: true,
    map: (value) => value.trim(),
    validate: (value) => Boolean(value) && value.length > 0 ? true : `Group label is required`
  },
  { 
    value: 'raceParty', 
    label: 'Race Party', 
    required: true,
    map: mapParty,
    validate: (value) => validateParty(value, true)
  },
  { 
    value: 'party', 
    label: 'Party', 
    required: true,
    map: mapParty,
    validate: (value) => validateParty(value, false)
  },
  { 
    value: 'ballotOrder', 
    label: 'Ballot Order',
  },
  { 
    value: 'maxChoices', 
    label: 'Plurality',
    map: (value) => {
      // cast to a number if it its a number
      if(!isNaN(parseInt(value))) {
        return Number(value);
      }
      return value;
    },
    validate: (value) => { 
      if(isNaN(value)) {
        return `Invalid number: ${value}`;
      }
      return true;
    }
  },
  { 
    value: 'incumbent', 
    label: 'Incumbent',
    map: mapBoolean,
    validate: validateBoolean
  },
  { 
    value: 'withdrawn', 
    label: 'Withdrawn',
    map: mapBoolean,
    validate: validateBoolean
  },
  { 
    value: 'writeIn', 
    label: 'Write In',
    map: mapBoolean,
    validate: validateBoolean
  },
  { 
    value: 'address', 
    label: 'Address'
  },
  { 
    value: 'email', 
    label: 'Email',
    map: (value) => value.trim(),
  },
  { 
    value: 'phone', 
    label: 'Phone',
    map: (value) => value.trim(),
  },
  { 
    value: 'secondaryEmail', 
    label: 'Secondary Email',
    map: (value) => value.trim(),
  },
  { 
    value: 'secondaryPhone', 
    label: 'Secondary Phone',
    map: (value) => value.trim(),
  },
  { 
    value: 'website', 
    label: 'Website',
    map: (value) => {
      const trimmed = String(value ?? '').trim().toLowerCase();
      if (['n/a', 'none', 'no', 'na','null', 'undefined'].includes(trimmed)) {
        return null;
      }
      return trimmed;
    },
    validate: validateWebsite
  },
  { 
    value: 'occupation', 
    label: 'Occupation'
  },
];

export const getCandidateListColumnMappingOptions = (
  supportedPartyKeys = [],
  partyOptions = []
) => candidateListColumnMappingOptions.map((option) => {
  if (option.value !== 'party' && option.value !== 'raceParty') {
    return option;
  }

  return {
    ...option,
    map: (value) => mapPartyWithOptions(value, partyOptions),
    validate: (value) => validatePartyWithAllowedList(value, supportedPartyKeys)
  };
});